import * as React from 'react';
import { FormattedMessage, FormattedNumber } from '@liveauctioneers/hammer-ui-core/intl';
import { getDiscoverPaginationRecordsCount } from '@/redux/modules/discover';
import { H4, H6 } from '@liveauctioneers/hammer-ui-core/text';
import { parseQueryParams } from '@/utils/queryParams';
import { SearchSortAndDirection } from '@/types/search/enums/sortTypes';
import { Sparkle, Truck } from '@liveauctioneers/hammer-ui-core/icons';
import { toQueryString } from '@/utils/urls';
import { useAppSelector } from '@/redux/hooks';
import { useLocation, useNavigate } from 'react-router-dom';
import Flag from '@liveauctioneers/caterwaul-components/lib/Intl/Flag';
import normalizeQuery from './helpers/normalizeQuery';
import SearchSort from '@/components/SearchResults/SearchSort';
import styled from 'styled-components';
const DEFAULT_PAGE_SORT = SearchSortAndDirection.MostRelevant;

const SORT_OPTIONS = [
    SearchSortAndDirection.MostRelevant,
    SearchSortAndDirection.NewlyListed,
    SearchSortAndDirection.TimeMostRecent,
    SearchSortAndDirection.MostBids,
    SearchSortAndDirection.EstimateHighest,
    SearchSortAndDirection.PriceHighest,
    SearchSortAndDirection.LotNumberLowest,
];

interface QueryParams {
    country?: string;
    publishDateMin?: string;
    shipping?: string;
    sort?: SearchSortAndDirection;
    topRated?: string;
}

const DiscoverPageHeader = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const selectId = React.useId();
    const totalRecords = useAppSelector(getDiscoverPaginationRecordsCount);
    const params: QueryParams = parseQueryParams(location.search);
    const { country, publishDateMin, shipping, sort, topRated } = params;

    const navigateTo = React.useCallback(
        (param: QueryParams) => {
            const newParams = normalizeQuery({ ...params, ...param });
            navigate({ search: toQueryString(newParams) });
        },
        [navigate, params]
    );

    const handleSortUpdate = React.useCallback((sort: SearchSortAndDirection) => navigateTo({ sort }), [navigateTo]);

    const handleShippingFilter = React.useCallback(
        () =>
            navigateTo({
                shipping: shipping === 'inHouseShipping' ? '' : 'inHouseShipping',
            }),
        [navigateTo, shipping]
    );

    const handleUsFilter = React.useCallback(
        () => navigateTo({ country: country === 'US' ? '' : 'US' }),
        [navigateTo, country]
    );

    const handleNewFilter = React.useCallback(() => {
        const twoDaysAgo = Math.floor((Date.now() - 172800000) / 1000);
        navigateTo({
            publishDateMin: Boolean(publishDateMin) ? '' : `${twoDaysAgo}`,
        });
    }, [navigateTo, publishDateMin]);

    const handleTopRatedFilter = React.useCallback(
        () => navigateTo({ topRated: topRated === 'topRated' ? '' : 'topRated' }),
        [navigateTo, topRated]
    );

    return (
        <>
            <StyledButtonBar>
                <StyledButton
                    $active={Boolean(shipping)}
                    onClick={handleShippingFilter}
                >
                    <Truck
                        className="mr-[12px] h-[24px] w-[24px]"
                        style="fill"
                    />
                    <H6>
                        <FormattedMessage id="discoverPage.buttons.shipping" />
                    </H6>
                </StyledButton>
                <StyledButton
                    $active={Boolean(country)}
                    onClick={handleUsFilter}
                >
                    <StyledFlag countryCode="US" />
                    <H6>
                        <FormattedMessage id="discoverPage.buttons.us" />
                    </H6>
                </StyledButton>
                <StyledButton
                    $active={Boolean(publishDateMin)}
                    onClick={handleNewFilter}
                >
                    <Sparkle
                        className="mr-[12px] h-[24px] w-[24px]"
                        size="sm"
                        style="fill"
                    />
                    <H6>
                        <FormattedMessage id="discoverPage.buttons.new" />
                    </H6>
                </StyledButton>
                <StyledButton
                    $active={Boolean(topRated)}
                    onClick={handleTopRatedFilter}
                >
                    <StyledImage
                        alt="badge"
                        src="https://p1.liveauctioneers.com/dist/images/badge.svg"
                    />
                    <H6>
                        <FormattedMessage id="discoverPage.buttons.top" />
                    </H6>
                </StyledButton>
            </StyledButtonBar>
            <StyledBar>
                <H4>
                    <FormattedMessage
                        id="search_alerts.resultsPlaceholder"
                        key="discover-page-header-total-records-placeholder"
                        values={{
                            count: (
                                <FormattedNumber
                                    key="total_records_placeholder"
                                    value={totalRecords}
                                />
                            ),
                        }}
                    />
                </H4>
                <SearchSort
                    archivedSearch={false}
                    instanceId={selectId}
                    sort={sort || DEFAULT_PAGE_SORT}
                    sortOptions={SORT_OPTIONS}
                    sortSelected={handleSortUpdate}
                />
            </StyledBar>
        </>
    );
};

export default DiscoverPageHeader;

const StyledBar = styled.div`
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
    height: 70px;
    border-top: 1px solid ${({ theme }) => theme.colors.grey400};
    border-bottom: 1px solid ${({ theme }) => theme.colors.grey400};
    margin: 0 0 28px;
`;
const StyledButtonBar = styled.div`
    display: flex;
    flex-direction: row;
    justify-content: flex-start;
    align-items: center;
    padding: 30px 0;
    overflow-y: hidden;
    overflow-x: auto;

    @media (max-width: ${({ theme }) => theme.breakpoints.tabletNarrowWidth}) {
        padding: 15px 0;
    }
`;

interface ButtonStyleProps {
    $active: boolean;
}

const StyledButton = styled.div<ButtonStyleProps>`
    height: 42px;
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
    white-space: nowrap;
    padding: 0 15px;
    color: ${({ theme }) => theme.colors.steel};
    border: 1px solid ${({ theme }) => theme.colors.grey400};
    background-color: ${({ theme }) => theme.colors.white};
    margin-right: 10px;
    cursor: pointer;
    transition: all 0.25s;
    ${({ $active, theme }) => ($active ? `background-color: ${theme.colors.grey400}` : '')};

    @media (max-width: ${({ theme }) => theme.breakpoints.tabletNarrowWidth}) {
        height: 30px;
        padding: 0 10px;
    }
`;

const StyledFlag = styled(Flag)`
    margin-right: 12px;
`;

const StyledImage = styled.img`
    height: 17px;
    width: 18px;
    margin-right: 12px;
`;
